import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Activity, Clock, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function Analytics() {
  const [tickets, setTickets] = useState([]);
  const [discoveries, setDiscoveries] = useState([]);
  const [agentTasks, setAgentTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const ticketsRes = await axios.get(`${API}/tickets`);
      setTickets(ticketsRes.data);

      // Fetch agent tasks for all tickets
      const taskPromises = ticketsRes.data.map(async (ticket) => {
        try {
          const tasksRes = await axios.get(`${API}/agents/${ticket.ticket_number}/tasks`);
          return tasksRes.data;
        } catch (err) {
          return [];
        }
      });

      const taskResults = await Promise.all(taskPromises);
      setAgentTasks(taskResults.flat());

      // Fetch discoveries
      const discoveryPromises = ticketsRes.data.map(async (ticket) => {
        try {
          const discoveryRes = await axios.get(`${API}/discovery/${ticket.ticket_number}`);
          return discoveryRes.data;
        } catch (err) {
          return null;
        }
      });

      const discoveryResults = await Promise.all(discoveryPromises);
      setDiscoveries(discoveryResults.filter(d => d !== null));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  // Ticket Status Distribution
  const statusDistribution = tickets.reduce((acc, ticket) => {
    const status = ticket.status.replace('_', ' ');
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const statusData = Object.keys(statusDistribution).map(key => ({
    name: key,
    value: statusDistribution[key]
  }));

  // Priority Distribution
  const priorityDistribution = tickets.reduce((acc, ticket) => {
    acc[ticket.priority] = (acc[ticket.priority] || 0) + 1;
    return acc;
  }, {});

  const priorityData = Object.keys(priorityDistribution).map(key => ({
    name: key,
    count: priorityDistribution[key]
  }));

  // Agent Performance
  const agentPerformance = agentTasks.reduce((acc, task) => {
    const agent = task.agent_type;
    if (!acc[agent]) {
      acc[agent] = { name: agent, completed: 0, failed: 0, in_progress: 0, pending: 0 };
    }
    acc[agent][task.status.replace('_', ' ')] = (acc[agent][task.status.replace('_', ' ')] || 0) + 1;
    return acc;
  }, {});

  const agentData = Object.values(agentPerformance);

  // Tickets over time (mock data for trend)
  const ticketTrend = [
    { month: 'Jan', tickets: 45, completed: 38 },
    { month: 'Feb', tickets: 52, completed: 45 },
    { month: 'Mar', tickets: 48, completed: 42 },
    { month: 'Apr', tickets: 61, completed: 55 },
    { month: 'May', tickets: 55, completed: 50 },
    { month: 'Jun', tickets: tickets.length, completed: tickets.filter(t => t.status === 'completed').length }
  ];

  // Clean Core Compliance over time
  const complianceData = discoveries.map((d, idx) => ({
    scan: `Scan ${idx + 1}`,
    compliant: d.clean_core_compliant,
    nonCompliant: d.non_compliant
  }));

  // SAP System Distribution
  const systemDistribution = tickets.reduce((acc, ticket) => {
    acc[ticket.sap_system] = (acc[ticket.sap_system] || 0) + 1;
    return acc;
  }, {});

  const systemData = Object.keys(systemDistribution).map(key => ({
    name: key,
    value: systemDistribution[key]
  }));

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Analytics & Reporting</h1>
        <p className="text-muted-foreground mt-2">Comprehensive insights and performance metrics</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
            <Activity className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tickets.length}</div>
            <p className="text-xs text-muted-foreground">+12% from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tickets.length > 0 ? ((tickets.filter(t => t.status === 'completed').length / tickets.length) * 100).toFixed(1) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">+5% from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Delivery Time</CardTitle>
            <Clock className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">2.5 days</div>
            <p className="text-xs text-muted-foreground">-30% from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Agent Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {agentTasks.length > 0 ? ((agentTasks.filter(t => t.status === 'completed').length / agentTasks.length) * 100).toFixed(1) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">+8% from last month</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="tickets" className="w-full">
        <TabsList>
          <TabsTrigger value="tickets">Tickets</TabsTrigger>
          <TabsTrigger value="agents">Agents</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="systems">Systems</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Ticket Status Distribution</CardTitle>
                <CardDescription>Current status breakdown of all tickets</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Priority Distribution</CardTitle>
                <CardDescription>Tickets by priority level</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={priorityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Ticket Trend</CardTitle>
              <CardDescription>Monthly ticket creation and completion rates</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={ticketTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="tickets" stroke="#3b82f6" strokeWidth={2} name="Created" />
                  <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} name="Completed" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Agent Performance</CardTitle>
              <CardDescription>Task execution status by agent type</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={agentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completed" stackId="a" fill="#10b981" name="Completed" />
                  <Bar dataKey="in progress" stackId="a" fill="#3b82f6" name="In Progress" />
                  <Bar dataKey="failed" stackId="a" fill="#ef4444" name="Failed" />
                  <Bar dataKey="pending" stackId="a" fill="#94a3b8" name="Pending" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-5">
            {['design', 'dev', 'test', 'devops', 'governance'].map((agent) => {
              const agentStats = agentTasks.filter(t => t.agent_type === agent);
              const completed = agentStats.filter(t => t.status === 'completed').length;
              const total = agentStats.length;
              const successRate = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;

              return (
                <Card key={agent}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium capitalize">{agent} Agent</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{successRate}%</div>
                    <p className="text-xs text-muted-foreground mt-1">{completed}/{total} tasks completed</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="compliance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Clean Core Compliance Trend</CardTitle>
              <CardDescription>Compliance vs non-compliance findings across scans</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={complianceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="scan" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="compliant" fill="#10b981" name="Compliant" />
                  <Bar dataKey="nonCompliant" fill="#ef4444" name="Non-Compliant" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Customizations Scanned</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {discoveries.reduce((sum, d) => sum + d.total_customizations, 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Across {discoveries.length} scans</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Clean Core Compliant</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {discoveries.reduce((sum, d) => sum + d.clean_core_compliant, 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Following SAP best practices</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Non-Compliant</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {discoveries.reduce((sum, d) => sum + d.non_compliant, 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Requires remediation</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="systems" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>SAP System Distribution</CardTitle>
                <CardDescription>Tickets by SAP system type</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={systemData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {systemData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>System Statistics</CardTitle>
                <CardDescription>Breakdown by system</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {systemData.map((system, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                        <span className="text-sm font-medium">{system.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground">{system.value} tickets</span>
                        <span className="text-sm font-semibold">
                          {tickets.length > 0 ? ((system.value / tickets.length) * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
